 
'use client';

import * as React from 'react';
import type { DialogProps } from '@radix-ui/react-dialog';
import { Command as CommandPrimitive, useCommandState } from 'cmdk';
import { IconArrowUp } from 'icon/Arrows/arrow-up';
import { IconCornerDownLeft } from 'icon/Arrows/corner-down-left';
import { IconLoading02 } from 'icon/General/loading-02';
import { IconSearchLg } from 'icon/General/search-lg';
import { cn, isMac } from '../utils';
import { Dialog, DialogContent } from './dialog';

const Command = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive>
>(({ className, ...props }, ref) => (
  <CommandPrimitive
    className={cn(
      'flex h-full w-full flex-col overflow-hidden rounded-md bg-white text-gray-950 dark:bg-gray-950 dark:text-gray-50',
      className,
    )}
    ref={ref}
    {...props}
  />
));
Command.displayName = CommandPrimitive.displayName;

interface CommandDialogProps extends DialogProps {}

function CommandDialog({ children, ...props }: CommandDialogProps) {
  return (
    <Dialog {...props}>
      <DialogContent
        className="max-w-xl! overflow-hidden p-0! shadow-lg"
        showCloseButton={false}
      >
        <Command className="**:[[cmdk-group-heading]]:px-2 **:[[cmdk-group-heading]]:font-medium **:[[cmdk-group-heading]]:text-gray-500 dark:**:[[cmdk-group-heading]]:text-gray-400 [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 **:[[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 **:[[cmdk-input]]:h-12">
          {children}
          <footer className="flex items-center gap-4 border-t bg-white/80 px-[18px] py-3 text-gray-500 backdrop-blur-md">
            <p className="flex items-center gap-1.5 text-sm font-semibold">
              <kbd className="rounded-md border bg-gray-50 p-1.5">
                <IconArrowUp height={16} width={16} />
              </kbd>
              <kbd className="rounded-md border bg-gray-50 p-1.5">
                <IconArrowUp className="rotate-180" height={16} width={16} />
              </kbd>
              <span className="ps-0.5">to navigate</span>
            </p>
            <p className="flex items-center gap-2 text-sm font-semibold">
              <kbd className="rounded-md border bg-gray-50 p-1.5">
                <IconCornerDownLeft height={16} width={16} />
              </kbd>
              <span>to select</span>
            </p>
            <p className="flex items-center gap-2 text-sm font-semibold">
              <kbd className="rounded-md border bg-gray-50 p-1.5 font-mono font-medium">
                esc
              </kbd>
              <span>to close</span>
            </p>
          </footer>
        </Command>
      </DialogContent>
    </Dialog>
  );
}

const CommandInput = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Input>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input>
>(({ className, ...props }, ref) => (
  <div className="flex items-center border-b px-3" cmdk-input-wrapper="">
    <IconSearchLg
      className="me-2 shrink-0 text-gray-500"
      height={20}
      width={20}
    />
    <CommandPrimitive.Input
      className={cn(
        'flex h-11 w-full rounded-md bg-transparent py-3 text-md text-gray-900 outline-hidden placeholder:text-gray-500 disabled:cursor-not-allowed disabled:opacity-50 dark:placeholder:text-gray-400',
        className,
      )}
      ref={ref}
      {...props}
    />
    <kbd className="pointer-events-none inline-flex select-none items-center gap-0.5 rounded-md border bg-gray-50 px-1.5 py-1 font-sans text-sm font-medium text-gray-600">
      <span className="">{isMac() ? '⌘' : 'Ctrl'}</span>K
    </kbd>
  </div>
));

CommandInput.displayName = CommandPrimitive.Input.displayName;

const CommandList = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.List>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.List
    className={cn('max-h-[300px] overflow-y-auto overflow-x-hidden', className)}
    ref={ref}
    {...props}
  />
));

CommandList.displayName = CommandPrimitive.List.displayName;

const CommandEmpty = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Empty>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Empty>
>((props, ref) => (
  <CommandPrimitive.Empty
    className="py-6 text-center text-sm"
    ref={ref}
    {...props}
  />
));

CommandEmpty.displayName = CommandPrimitive.Empty.displayName;

const CommandGroup = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Group>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Group>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Group
    className={cn(
      'overflow-hidden p-1 text-gray-950 dark:text-gray-50',
      // Group heading
      '**:[[cmdk-group-heading]]:px-2 **:[[cmdk-group-heading]]:py-1.5 **:[[cmdk-group-heading]]:text-sm **:[[cmdk-group-heading]]:font-medium **:[[cmdk-group-heading]]:text-gray-600 dark:**:[[cmdk-group-heading]]:text-gray-400',
      className,
    )}
    ref={ref}
    {...props}
  />
));

CommandGroup.displayName = CommandPrimitive.Group.displayName;

const CommandSeparator = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Separator
    className={cn('-mx-1 h-px bg-gray-200 dark:bg-gray-800', className)}
    ref={ref}
    {...props}
  />
));
CommandSeparator.displayName = CommandPrimitive.Separator.displayName;

const CommandItem = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Item>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Item
    className={cn(
      'relative flex cursor-default select-none items-center rounded-sm px-2.5 py-3 text-sm font-semibold outline-hidden aria-selected:bg-gray-100 aria-selected:text-gray-900 data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50 dark:aria-selected:bg-gray-800 dark:aria-selected:text-gray-50',
      className,
    )}
    ref={ref}
    {...props}
  />
));

CommandItem.displayName = CommandPrimitive.Item.displayName;

function CommandShortcut({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) {
  return (
    <span
      className={cn(
        'ml-auto text-xs tracking-widest text-gray-500 dark:text-gray-400',
        className,
      )}
      {...props}
    />
  );
}
CommandShortcut.displayName = 'CommandShortcut';

const CommandLoading = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Loading>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Loading>
>(({ className, children, ...props }, ref) => (
  <CommandPrimitive.Loading
    className={cn('px-4 py-4 text-sm text-gray-500', className)}
    ref={ref}
    {...props}
  >
    <div className="flex flex-row items-center justify-center text-sm font-medium">
      <IconLoading02 className="me-2 animate-spin" height={20} width={20} />
      {children}
    </div>
  </CommandPrimitive.Loading>
));

CommandLoading.displayName = CommandPrimitive.Loading.displayName;

export {
  Command,
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
  CommandSeparator,
  CommandLoading,
  useCommandState,
};
